import {BaseElement, BASIS, MsgLabel, OSkinableInit} from "/@back@/commons/basis.js";
import {Button, ButtonActions} from "/@back@/commons/widgets/buttons.js";
import {OPopupDialogInit, POPUP} from "/@back@/commons/widgets/popups.js";
import {SpaceSelector} from "/@back@/wsp/dialogs/spaceSelector.js";
import {ItemTypesTree, OItemTypesTreeInit} from "/@back@/wsp/widgets/itemTypesTree.js";
import {Action} from "/@lib@/commons/actions.js";
import {EHttpStatusCode, IBody, RespError} from "/@lib@/commons/io/io.js";
import {IReg, REG} from "/@lib@/commons/registry.js";
import {DOM, JSX} from "/@lib@/commons/xml/dom.js";
import {DOMSH} from "/@lib@/commons/xml/domsh.js";
import {FolderEntries, ITEM} from "/@lib@/wsp/item.js";
import {JSrcFields, JSrcFieldsTree, SRC, srcUri} from "/@lib@/wsp/src.js";
import {IWspUiEnv, Wsp} from "/@lib@/wsp/wsp.js";
import {EItemTypeFamily, ItemType, ONewContent} from "/@lib@/wsp/wspMetaUi.js";
import {IFolder, isFolder} from "/@lib@/commons/io/files.js";
import {WspImportSrc} from "/@back@/wsp/actions/srcActions.js";
import {IPopupable} from "/@back@/commons/widgets/popupable.js";

export interface OItemCreatorInit extends OSkinableInit {

	reg?: IReg<IWspUiEnv>

	/**
	 * editable : l'espace d'insertion peut être modifié
	 * readOnly : l'espace ne peut être modifié mais est affiché (space doit être renseigné)
	 * hidden : l'espace n'est pas affiché  (space doit être renseigné, sauf si airItem=forced)
	 */
	spaceUi?: 'editable' | 'readOnly' | 'hidden'
	spaceUri?: srcUri | JSrcFields //JSrcFields si connu pour verif perms.

	/**
	 * never : airItem non autorisés [par defaut]
	 * forced : on créé toujours un airItem, force spaceUi à 'hidden'
	 * prefered : sélectionne par défaut le airItem, mais le user peut choisir le mode espace
	 * allowed : privillégie la création dans un espace, mais autorise la création en airItem
	 */
	airItem?: 'never' | 'forced' | 'prefered' | 'allowed'

	/**
	 * editable : le code de l'item est modifiable
	 * readOnly : le code de l'item est affiché mais non éditable
	 * hidden : le code de l'item est masqué
	 * function : état dépendant de l'itemType courant sélectionné
	 */
	codeItemUi?: 'editable' | 'readOnly' | 'hidden' | ((itemType: ItemType | null) => 'editable' | 'readOnly' | 'hidden')

	/** Code de l'item à utiliser avec son extension. Si l'extension ne matche pas le type d'item, cette valeur n'est pas utilisée. */
	codeItem?: string

	/** Construction custom du nom de l'item (name de l'item sans son extension) proposé coté UI
	 * @return undefined : les comportements par défaut sont alors appliqués */
	nameBuilder?: (this: ItemCreator) => string | undefined

	/** Construction custom du code de l'item (code de l'item avec son extension), appelée juste avant l'ordre de création
	 * La résolution à ce niveau permet entre autre d'interroger des compteurs d'ID
	 * @return undefined : les comportements par défaut sont alors appliqués (nameBuilder, ...) */
	codeItemBuilderBeforeSave?: (this: ItemCreator) => Promise<string | undefined>

	itemTypesTree?: OItemTypesTreeInit

	newContentOptions?: ONewContent

	/**
	 * Contenu prédéfini (import externes...).
	 * 1. Si function, évalue d'abord la function.
	 * 2. Si null, interroge ItemType.getNewContent().
	 * 3. Si le résultat final est null, créée un dossier vide; retourner "" pour un fichier vide.
	 */
	body?: IBody | IFolder | null | ((wsp: Wsp, itemType: ItemType, space: null | string, codeItem: string, options?: ONewContent) => Promise<IBody | IFolder | null>);
}

export interface ItemCreator extends BaseElement {
	initialize(init: OItemCreatorInit): this
}

export class ItemCreator extends BaseElement {

	static openCreateItem(init: OItemCreatorInit, titleDialog: string, uiContext: HTMLElement, ev?: Event): IPopupable<JSrcFields | null> {
		const ct = new ItemCreator().initialize(init);
		const dialogInit: OPopupDialogInit = {titleBar: titleDialog, resizer: {}, viewPortY: '1/3', initMaxHeight: '70vh', initWidth: 'min(60em, 80vw)'};
		return POPUP.showDialog(ct, uiContext, dialogInit);
	}

	/** Configure l'itemCreator en fonction des prefs du registre. */
	static setDefaultConfigFromReg(reg: IReg<IWspUiEnv>, cnf?: OItemCreatorInit): OItemCreatorInit {
		if (!cnf) cnf = {};
		if (!("reg" in cnf)) cnf.reg = reg;
		if (!("spaceUi" in cnf)) cnf.spaceUi = reg.getPref("ItemCreator.spaceUi");
		if (!("airItem" in cnf)) cnf.airItem = reg.env.wsp.isAirItem ? reg.getPref("ItemCreator.airItem", 'allowed') : 'never';
		if (!("codeItemUi" in cnf)) cnf.codeItemUi = reg.getPref("ItemCreator.codeItemUi");
		if (!("nameBuilder" in cnf)) cnf.nameBuilder = reg.getPref("ItemCreator.nameBuilder");
		if (!("codeItemBuilderBeforeSave" in cnf)) cnf.codeItemBuilderBeforeSave = reg.getPref("ItemCreator.codeItemBuilderBeforeSave");
		return cnf;
	}

	/** Contexte */
	reg: IReg<IWspUiEnv>;

	body?: IBody | IFolder | null | ((wsp: Wsp, itemType: ItemType, space: null | string, codeItem: string, options?: ONewContent) => Promise<IBody | IFolder | null>);
	contentType?: string;
	originalCode: string;
	nameBuilder?: (this: ItemCreator) => string;
	codeItemBuilderBeforeSave?: (this: ItemCreator) => Promise<string | undefined>;
	codeItemUi?: 'editable' | 'readOnly' | 'hidden' | ((itemType: ItemType | null) => 'editable' | 'readOnly' | 'hidden')
	newContentOptions?: ONewContent;

	get wsp(): Wsp {return this.reg.env.wsp};

	/** Eléments UI */
	itemTypesTree: ItemTypesTree;
	spaceLabel: HTMLSpanElement;
	spaceRadio: HTMLInputElement;
	codeItemArea: HTMLElement;
	nameInput: HTMLInputElement;
	extLabel: HTMLSpanElement;
	extPopup: ButtonActions<ItemCreator>;
	msgArea: MsgLabel;
	createBtn: Button;
	cancelBtn: Button;

	/** Etats internes */
	itemType: ItemType;
	space: srcUri | JSrcFields;

	get spaceUri() {return typeof this.space === "object" ? this.space.srcUri : this.space}

	airItem: boolean;
	codeItem: string;

	get ext(): string {return this.extLabel.textContent}

	/** Controle de l'existance des items. */
	spaceEntries: FolderEntries;


	protected _initialize(init: OItemCreatorInit) {
		this.reg = this.findReg(init);
		this.body = init.body;
		this.newContentOptions = init.newContentOptions;
		if (this.body instanceof Blob) this.contentType = this.body.type;
		const sr = this.attachShadow(DOMSH.SHADOWDOM_INIT);
		this.reg.installSkin("webzone:panel", sr);
		this.reg.installSkin("standard-dialog", sr);
		this._initAndInstallSkin(this.localName, init);

		if (this.contentType?.startsWith("image/")) {
			const img = sr.appendChild(<img id="preview"/>) as HTMLImageElement;
			img.src = URL.createObjectURL(this.body as Blob);
			this.classList.add("preview");
		}

		// wrapper de init.itemTypesTree.itemTypeReducer pour filtrer les itemType[noCreator]
		const itemTypeTreeInit = BASIS.newInit(init.itemTypesTree, this.reg)
		const itemTypeReducerBase = itemTypeTreeInit.itemTypeReducer;
		itemTypeTreeInit.itemTypeReducer = (acc: ItemType[], cur: ItemType, idx?: number, src?: ItemType[]): ItemType[] => {
			if (cur.datas.noCreator) return acc;
			if (cur.datas.permsCreator && !this.reg.hasPerm(cur.datas.permsCreator)) return acc;
			if (itemTypeReducerBase) return itemTypeReducerBase.call(this, acc, cur, idx, src);
			else {
				acc.push(cur);
				return acc;
			}
		}

		this.itemTypesTree = sr.appendChild(new ItemTypesTree().initialize(itemTypeTreeInit));
		this.addEventListener('grid-select', this.onItemTypeSelected.bind(this));
		const airItem = init.airItem || 'never';
		const switchSpaceAir = airItem === 'prefered' || airItem === 'allowed';
		if (airItem !== 'forced' && init.spaceUi !== 'hidden') {
			//switchSpaceAir ? spaceRoot.appendChild(<input type="radio" id="space">Espace :</input>) :
			const ctn = sr.appendChild(<div id="space"/>);
			if (switchSpaceAir) this.spaceRadio = ctn.appendChild(<input type="radio" name="spOrAir" id="radioSpace" onchange={this.onSwitchAirItem}/>) as HTMLInputElement;
			ctn.appendChild(<label for="radioSpace">Espace :</label>);
			if (init.spaceUi !== 'readOnly') {
				this.spaceLabel = ctn.appendChild(<c-button id="spaceLabel" onclick={this.onChooseSpace} title="Sélectionner un espace..." role="menu" ui-context="dialog">...</c-button>);
			} else {
				this.spaceLabel = ctn.appendChild(<span id="spaceLabel"/>);
			}
		}
		if (switchSpaceAir) {
			sr.appendChild(<div id="airItem">
				<input type="radio" name="spOrAir" id="radioAir" onchange={this.onSwitchAirItem}/>
				<label for="radioAir">~Item flottant~</label>
			</div>);
		}
		if (init.nameBuilder) this.nameBuilder = init.nameBuilder;
		if (init.codeItemUi) this.codeItemUi = init.codeItemUi;
		if (init.codeItemBuilderBeforeSave) this.codeItemBuilderBeforeSave = init.codeItemBuilderBeforeSave;

		this.originalCode = this.codeItem = init.codeItem;
		if (init.codeItemUi !== 'hidden') {
			this.codeItemArea = sr.appendChild(<div id="codeItem">Code :</div>);
			this.nameInput = this.codeItemArea.appendChild(<input id="codeInput" autocomplete="off" spellcheck="false" oninput={this.onCodeInput}/>) as HTMLInputElement;
			this.nameInput.readOnly = init.codeItemUi === 'readOnly';
			this.extLabel = this.codeItemArea.appendChild(<span id="extLabel"/>) as HTMLInputElement;
		}
		const footer = sr.appendChild(<div id="footer"/>);
		this.msgArea = footer.appendChild(<c-msg id="msg"/>) as MsgLabel;
		this.createBtn = footer.appendChild(<Button id="create" label="Créer" class="default" ui-context="dialog" onclick={this.onCreateBtn}/>) as Button;
		this.cancelBtn = footer.appendChild(<Button id="cancel" label="Annuler" ui-context="dialog" onclick={this.onCancelBtn}/>) as Button;
		if (airItem === 'forced') {
			this.airItem = true;
		} else if (airItem === 'prefered') {
			this.airItem = true;
			(sr.getElementById('radioAir') as HTMLInputElement).checked = true;
		} else {
			this.setSpace(init.spaceUri || "");
		}
		this.onkeypress = this.onKeypress;
		if (this.itemTypesTree.dataHolder.countRows() === 0) {
			POPUP.confirm("Aucun type d'item compatible n'a pu être sélectionné, la création est impossible.", this, {kind:"forbidden", cancelLbl: null, titleBar:"Création d'item impossible"}).then(() => {
				POPUP.findPopupableParent(this).close(null);
			})
			return;
		}
	}

	protected _refresh() {
		//appelé une fois inséré dans l'arbre.
		this.itemTypesTree.selectFirst();
		Promise.resolve().then(() => {
			//En asynch car dialog.showModal() écrase le focus.
			const countItemTypes = this.itemTypesTree.countItemTypesUntil(2);
			if (countItemTypes === 0) {
				//POPUP.showNotifWarning("Aucun type d'item compatible", this.reg.env.uiRoot);
				this.createBtn.disabled = true;
				this.nameInput.disabled = true;
			} else if (countItemTypes === 1) {
				// On masque la zone de sélection d'IT. Raisons :
				// - zone peu utile car sans effet (juste informative)
				// - info **fausse** dans le cas des items XML (@see srcActions, wspImportItem)
				DOM.setHidden(this.itemTypesTree, true);
				if (this.nameInput) this.nameInput.focus();
			} else {
				const toFocus = DOMSH.findFlatNext(this.shadowRoot, this.shadowRoot, DOM.IS_focusable);
				if (toFocus) toFocus.focus();
			}
		});
	}

	async createItem(): Promise<JSrcFields | null> {
		switch (this.evalCreatable()) {
		case 'no':
		case 'working':
			return null;
		case 'create':
			let bodyTpl = this.body;
			let body: IBody | null = null;
			let folder: IFolder;
			if (typeof bodyTpl === "function") {
				bodyTpl = await bodyTpl(this.wsp, this.itemType, this.airItem ? null : this.spaceUri, this.codeItem, this.newContentOptions);
			}
			if (!bodyTpl) {
				bodyTpl = await this.itemType.getNewContent(this.reg, this.airItem ? null : this.spaceUri, this.codeItem, this.newContentOptions);
				if (bodyTpl == null) return null;
			}
			if (isFolder(bodyTpl)) {
				folder = bodyTpl;
			} else {
				body = bodyTpl;
			}
			let srcFields: JSrcFields;
			const cancelLabel = this.cancelBtn.label;
			try {
				this.msgArea.setStandardMsg("saving");
				this.createBtn.disabled = true;
				this.cancelBtn.label = "Fermer";
				let codeItem = this.codeItem;
				if (this.codeItemBuilderBeforeSave) {
					const codeItemBuilderBeforeSave = await this.codeItemBuilderBeforeSave.call(this);
					if (codeItemBuilderBeforeSave !== undefined) codeItem = codeItemBuilderBeforeSave;
				}
				if (this.airItem) {
					srcFields = await ITEM.createAirItem(this.wsp, null, this, body, {srcNm: codeItem});
				} else {
					srcFields = await ITEM.createItem(this.wsp, null, this, body, SRC.addLeafToUri(this.spaceUri, codeItem));
				}
				if (folder) {
					const renameRes = codeItem !== folder.name && this.wsp.wspMetaUi.getItemType(srcFields.itModel)?.getFamily() === EItemTypeFamily.res;
					for (let entry of folder.entries) {
						if (entry instanceof File) {
							if (SRC.isValidPartUri(entry.name) === true) {
								await ITEM.update(this.wsp, this, SRC.addLeafToUri(srcFields.srcUri, renameRes && entry.name === folder.name ? this.codeItem : entry.name), entry);
							}
						} else {
							await WspImportSrc.pushResFolder(entry, srcFields.srcUri, this.wsp, this);
						}
					}
				}
			} catch (e) {
				if (e instanceof RespError) {
					switch (e.response.status) {
					case EHttpStatusCode.forbidden:
						POPUP.showNotifForbidden(this.airItem ? "Il est interdit de créer un item flottant." : "Il est interdit de créer un item à cet emplacement.", this);
						break;
					case EHttpStatusCode.unauthorized:
						POPUP.showNotifForbidden("Vous ne disposez pas des droits pour créer cet item.", this);
						break;
					default:
						POPUP.showNotifError(`Une erreur est survenue durant la création de cet item : ${e.response.status}`, this);
					}
				} else {
					POPUP.showNotifError("Une erreur est survenue durant la création de cet item.", this);
				}
				console.log(e);
			} finally {
				this.msgArea.setStandardMsg(null);
				this.createBtn.disabled = false;
				this.cancelBtn.label = cancelLabel;
			}
			return srcFields;
		}
	}

	async setExt(ext: string) {
		this.extLabel.textContent = ext;
		if (this.extPopup) this.extPopup.label = ext;
		this.setCreatableSt('working');
		let name: string = undefined;
		if (this.nameBuilder)
			name = this.nameBuilder();
		if (name === undefined) {
			if (this.originalCode && this.originalCode.toLowerCase().endsWith(ext.toLowerCase())) {
				name = this.originalCode.substr(0, this.originalCode.length - ext.length);
			} else {
				name = await this.itemType.buildItemName(this.wsp, this.airItem ? null : this.spaceUri, ext,
					this.airItem ? null : async (code) => await this.spaceEntries.getChildState(code) !== 'used');
			}
		}
		if (this.nameInput) this.nameInput.value = name;
		this.buildCodeItem(name, ext);
	}

	setSpace(space: srcUri | JSrcFields) {
		let spaceUri = typeof space === "object" ? space.srcUri : space;
		if (ITEM.isSpecialUri(spaceUri)) space = spaceUri = SRC.URI_ROOT; //sécurité, pas de crétaion d'items dans des uri spéciales
		if (this.spaceRadio) this.spaceRadio.checked = true;
		if (this.spaceLabel) this.spaceLabel.textContent = ITEM.extractSpaceLabel(spaceUri);
		if (!this.spaceEntries) this.spaceEntries = new FolderEntries(this.wsp, this);
		this.space = space;
		this.airItem = false;
		if (typeof space === "string") {
			this.wsp.fetchShortDesc(space).then((sd) => {
				if (space !== this.space) return; //racecond
				this.space = sd;
				this.evalCreatable();
			})
		}
		this.spaceEntries.setFolderUri(spaceUri);
	}

	evalCreatable(): 'no' | 'create' | 'working' {
		let msg: string = "";
		let st: 'create' | 'working' = 'create';
		let level: 'info' | 'error' = 'info';
		if (typeof this.space === "object" && !this.reg.hasPermission("create.item", this.space.srcRoles, null, this.space.srcRi)) {
			msg = this.reg.hasSystemRights("create.item", this.space.srcRi) ? "Vous ne disposez pas des droits pour ajouter un item dans cet espace." : "Impossible d'ajouter un item dans cet espace.";
			level = "error";
		} else if (!this.itemType) {
			const countItemTypes = this.itemTypesTree.countItemTypesUntil(1);
			msg = countItemTypes === 0 ? "Aucun type d'item compatible" : "Sélectionnez un type d'item dans la liste";
		} else if (!this.codeItem) {
			msg = "Renseignez le code de l'item";
		} else {
			const r = ITEM.isValidCodeItem(this.codeItem);
			if (r !== true) {
				msg = r;
				level = "error";
			} else if (!this.airItem) {
				if (this.spaceEntries.loading) {
					st = 'working';
					this.spaceEntries.loading.then(() => {this.evalCreatable()});
				} else {
					if (this.spaceEntries.getChildStateNow(this.codeItem) === "used") {
						msg = "Cet item existe déjà, changez de code";
						level = "error";
					}
				}
			}
		}
		this.msgArea.setCustomMsg(msg, level);
		this.setCreatableSt(msg ? 'no' : st);
		return msg ? 'no' : st;
	}

	setCreatableSt(st: 'create' | 'no' | 'working') {
		this.createBtn.disabled = st !== 'create';
	}

	onItemTypeSelected(ev?: CustomEvent) {
		if (ev) ev.stopImmediatePropagation();
		const itemType = this.itemTypesTree.getSelectedItemType();
		if (itemType === this.itemType) return;
		this.itemType = itemType;
		if (itemType == null) {
			this.codeItem = null;
			if (this.codeItemArea) this.codeItemArea.style.visibility = 'hidden';
			this.evalCreatable();
		} else {
			if (typeof this.codeItemUi === "function" && this.codeItemArea) {
				const codeItemUi = this.codeItemUi(itemType);
				if (codeItemUi === 'hidden') this.codeItemArea.style.visibility = 'hidden';
				else this.codeItemArea.style.visibility = '';
				if (this.nameInput) this.nameInput.readOnly = codeItemUi === 'readOnly';
			} else if (this.codeItemArea)
				this.codeItemArea.style.visibility = '';

			const ext = itemType.getExtensions(this.contentType);
			if (Array.isArray(ext)) {
				if (!this.extPopup) this.extPopup = this.extLabel.parentElement.appendChild(<ButtonActions id="extPopup" î={{
					reg: this.reg,
					uiContext: 'dialog',
					actionContext: this
				}}/>) as ButtonActions<ItemCreator>;
				this.extPopup.actions = ext.map((e) => new ExtAction(e));
				this.setExt(ext[0]);
				DOM.setHidden(this.extLabel, true);
				this.extPopup.hidden = false;
			} else {
				DOM.setHidden(this.extLabel, false);
				if (this.extPopup) this.extPopup.hidden = true;
				this.setExt(ext || (this.originalCode && this.originalCode.indexOf(".") > -1 ? this.originalCode.substring(this.originalCode.lastIndexOf(".")) : null));
			}
		}
	}

	async onChooseSpace(this: HTMLButtonElement, ev: Event) {
		const itemCreator = DOMSH.findHost(this) as ItemCreator;
		const {SpaceSelector} = await import("/@back@/wsp/dialogs/spaceSelector.js");
		const ct = new SpaceSelector().initialize({
			reg: itemCreator.reg,
			startSel: itemCreator.spaceUri,
			spaceTree: {
				showRoot: true
			},
			selOnDblClick: true,
			onSelChange: (src: JSrcFieldsTree, spSel: SpaceSelector) => {
				if (!src) spSel.setMsg("Sélectionnez un espace.", false);
				else if (spSel.evalPerm("action.src#create.item", src, "Vous ne disposez pas des droits pour créer un item dans cet espace.", "Il est impossible de créer un item dans cet espace.")) {
					spSel.setMsg(null, true);
				}
			}
		});
		ct.style.width = "35em";
		const space = await POPUP.showMenuFromEvent<JSrcFields | undefined>(ct, ev, itemCreator, {}).onNextClose();
		if (space) {
			itemCreator.setSpace(space);
			itemCreator.evalCreatable();
		}
	}

	onSwitchAirItem(this: HTMLInputElement) {
		const itemCreator = DOMSH.findHost(this) as ItemCreator;
		itemCreator.airItem = !itemCreator.spaceRadio.checked;
		itemCreator.setExt(itemCreator.ext);
	}

	onCodeInput(this: HTMLInputElement, ev: Event) {
		const itemCreator = DOMSH.findHost(this) as ItemCreator;
		itemCreator.buildCodeItem(this.value, itemCreator.ext);
	}

	async onKeypress(ev: KeyboardEvent) {
		if (ev.key === "Enter") {
			const r = await this.createItem();
			if (r) POPUP.findPopupableParent(this).close(r);
		}
	}

	onCancelBtn(this: Button) {
		POPUP.findPopupableParent(this).close();
	}

	async onCreateBtn(this: HTMLElement) {
		const r = await DOMSH.findHost<ItemCreator>(this).createItem();
		if (r) POPUP.findPopupableParent(this).close(r);
	}

	protected buildCodeItem(name: string, ext: string) {
		this.codeItem = name + ext;
		this.evalCreatable();
	}
}

REG.reg.registerSkin('wsp-item-creator', 1, /* language=CSS */ `
	:host {
		min-height: 10vh;
	}

	:host(.preview) {
		min-height: 35vh;
	}

	wsp-itemtypes-tree {
		border-bottom: 1px solid var(--border-color);
		margin-bottom: .5em;
	}

	wsp-itemtypes-tree[hidden] {
		visibility: hidden;
		margin: 0;
	}

	#preview {
		align-self: center;
		max-width: 60vw;
		max-height: 15vh;
		margin: 2px;
	}

	#space {
		display: flex;
		min-height: 0;
		min-width: 0;
		align-items: center;
	}

	#space,
	#airItem {
		margin: .2em;
	}

	#codeItem {
		margin: 1em;
		display: flex;
		min-height: 0;
		min-width: 0;
		align-items: center;
	}

	:focus-visible {
		outline: var(--focus-outline);
	}

	#spaceLabel {
		margin: .5em;
	}

	#extPopup {
		display: inline-flex;
		font-weight: bold;
		margin: 0;
	}

	#extPopup[hidden] {
		display: none;
	}

	#codeInput {
		margin-inline-start: .5em;
		font-size: inherit;
		background-color: var(--form-bgcolor);
		color: var(--form-color);
		border: 1px solid var(--border-color);
	}

	#msg {
		flex: 1;
		margin: 0 .5em;
		justify-content: start;
	}

	input[readonly], #codeInput[readonly] {
		background-color: initial;
		border-color: transparent;
	}

`);

customElements.define("wsp-item-creator", ItemCreator);

class ExtAction extends Action<ItemCreator> {
	constructor(ext: string) {
		super(ext);
		this._label = ext;
	}

	execute(ctx: ItemCreator, ev?: Event) {
		ctx.setExt(this._id);
	}
}